/**
 * React Query hooks for Member Progress CRM
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MemberProgressService } from '@/services/domains/crm/member-progress.service';
import { MemberNotesService } from '@/services/domains/crm/member-notes.service';
import type {
  ProgressFilters,
  MemberProgressSummary,
  MemberProgressDetail,
  ProgressStats,
  CreateNoteInput,
  UpdateNoteInput,
  MemberNote
} from '@/types/crm.types';

// ============================================
// Query Keys
// ============================================

export const memberProgressKeys = {
  all: ['memberProgress'] as const,
  list: (clubId: string, filters?: ProgressFilters) =>
    [...memberProgressKeys.all, 'list', clubId, filters] as const,
  detail: (clubId: string, memberId: string) =>
    [...memberProgressKeys.all, 'detail', clubId, memberId] as const,
  notes: (clubId: string, memberId: string) =>
    [...memberProgressKeys.all, 'notes', clubId, memberId] as const,
};

// ============================================
// List View Hooks
// ============================================

/**
 * Hook to fetch all members with progress for the list view
 */
export function useMembersProgress(clubId: string | undefined, filters?: ProgressFilters) {
  return useQuery({
    queryKey: memberProgressKeys.list(clubId || '', filters),
    queryFn: () => MemberProgressService.getMembersProgress(clubId!, filters),
    enabled: !!clubId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ============================================
// Detail View Hooks
// ============================================

/**
 * Hook to fetch detailed progress for a single member
 */
export function useMemberProgressDetail(clubId: string | undefined, memberId: string | undefined) {
  return useQuery({
    queryKey: memberProgressKeys.detail(clubId || '', memberId || ''),
    queryFn: () => MemberProgressService.getMemberDetail(clubId!, memberId!),
    enabled: !!clubId && !!memberId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// ============================================
// Notes Hooks
// ============================================

/**
 * Hook to fetch notes for a member
 */
export function useMemberNotes(clubId: string | undefined, memberId: string | undefined) {
  return useQuery({
    queryKey: memberProgressKeys.notes(clubId || '', memberId || ''),
    queryFn: () => MemberNotesService.getNotesByMember(clubId!, memberId!),
    enabled: !!clubId && !!memberId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to create a new note
 */
export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateNoteInput) => MemberNotesService.createNote(input),
    onSuccess: (newNote, variables) => {
      // Invalidate notes query
      queryClient.invalidateQueries({
        queryKey: memberProgressKeys.notes(variables.club_id, variables.member_id)
      });
      // Invalidate detail query to update note count
      queryClient.invalidateQueries({
        queryKey: memberProgressKeys.detail(variables.club_id, variables.member_id)
      });
      // Invalidate list query to update note count badges in the member list view
      queryClient.invalidateQueries({
        queryKey: memberProgressKeys.list(variables.club_id, undefined)
      });
    },
  });
}

/**
 * Hook to update a note
 */
export function useUpdateNote(clubId: string, memberId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateNoteInput) => MemberNotesService.updateNote(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: memberProgressKeys.notes(clubId, memberId)
      });
    },
  });
}

/**
 * Hook to delete a note
 */
export function useDeleteNote(clubId: string, memberId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) => MemberNotesService.deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: memberProgressKeys.notes(clubId, memberId)
      });
      queryClient.invalidateQueries({
        queryKey: memberProgressKeys.detail(clubId, memberId)
      });
    },
  });
}

// ============================================
// Utility Types for Components
// ============================================

export type UseMembersProgressReturn = ReturnType<typeof useMembersProgress>;
export type UseMemberProgressDetailReturn = ReturnType<typeof useMemberProgressDetail>;
export type UseMemberNotesReturn = ReturnType<typeof useMemberNotes>;
